import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { rexChat, rexChatStream, AiUnavailableError } from '../lib/ai';
import { matchRexScreen, buildRexContextLabel } from '../lib/rexScreens';

const RexContext = createContext(null);

/**
 * Workspace-level Rex state. Wrap Layer 1/2/admin shells.
 * Child screens optionally call useRexScreen() to pass live snapshot data.
 */
export function RexProvider({ base = {}, children }) {
  const location = useLocation();
  const [screenOverride, setScreenOverride] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [error, setError] = useState(null);
  const [pathKey, setPathKey] = useState(location.pathname);
  const [panelOpenSignal, setPanelOpenSignal] = useState(0);
  const [panelCloseSignal, setPanelCloseSignal] = useState(0);

  const screen = useMemo(() => matchRexScreen(location.pathname), [location.pathname]);

  const suggestions = useMemo(
    () => screenOverride?.suggestions?.length ? screenOverride.suggestions : screen.suggestions,
    [screen, screenOverride]
  );

  const contextLabel = useMemo(
    () => screenOverride?.contextLabel || buildRexContextLabel(base, screen),
    [base, screen, screenOverride]
  );

  const apiContext = useMemo(() => ({
    orgName: base.orgName,
    programmeName: base.programmeName,
    projectName: base.projectName,
    layer: base.layer,
    pillar: screen.pillar,
    screenId: screen.id,
    path: location.pathname,
    snapshot: screenOverride?.snapshot || null,
  }), [base, screen, location.pathname, screenOverride]);

  // Reset conversation when navigating to a different screen
  useEffect(() => {
    if (location.pathname !== pathKey) {
      setPathKey(location.pathname);
      setMessages([]);
      setError(null);
      setStreamingText('');
      setScreenOverride(null);
    }
  }, [location.pathname, pathKey]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = text?.trim();
    if (!trimmed || loading) return;

    const userMsg = { role: 'user', content: trimmed };
    const nextHistory = [...messages, userMsg];
    setMessages(nextHistory);
    setLoading(true);
    setStreamingText('');
    setError(null);

    const isCharter = screen.id === 'charter';

    try {
      let reply;
      if (isCharter) {
        reply = await rexChatStream({
          messages: nextHistory,
          screen: screen.title,
          context: apiContext,
        }, {
          onToken: (chunk) => setStreamingText((prev) => prev + chunk),
        });
      } else {
        reply = await rexChat({
          messages: nextHistory,
          screen: screen.title,
          context: apiContext,
        });
      }
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (e) {
      const msg = e instanceof AiUnavailableError
        ? (e.message || 'Rex is unavailable.')
        : (e?.message || 'Something went wrong.');
      setError(msg);
    } finally {
      setStreamingText('');
      setLoading(false);
    }
  }, [loading, messages, screen.id, screen.title, apiContext]);

  const requestOpenPanel = useCallback(() => {
    setPanelOpenSignal((n) => n + 1);
  }, []);

  const requestClosePanel = useCallback(() => {
    setPanelCloseSignal((n) => n + 1);
  }, []);

  const apply = screenOverride?.apply || null;

  const value = useMemo(() => ({
    screen,
    contextLabel,
    suggestions,
    messages,
    loading,
    streamingText,
    error,
    priming: screenOverride?.priming || screen.priming,
    apply,
    charterDrafting: Boolean(screenOverride?.charterDrafting),
    charterDraftComplete: Boolean(screenOverride?.charterDraftComplete),
    sendMessage,
    setScreenOverride,
    hasSuggestions: suggestions.length > 0,
    panelOpenSignal,
    panelCloseSignal,
    requestOpenPanel,
    requestClosePanel,
  }), [screen, contextLabel, suggestions, messages, loading, streamingText, error, screenOverride, apply, sendMessage, panelOpenSignal, panelCloseSignal, requestOpenPanel, requestClosePanel]);

  return (
    <RexContext.Provider value={value}>
      {children}
    </RexContext.Provider>
  );
}

export function useRex() {
  const ctx = useContext(RexContext);
  if (!ctx) throw new Error('useRex must be used within RexProvider');
  return ctx;
}

/** Optional — enrich Rex with live screen data, suggestions, and confirm-before-apply actions. */
export function useRexScreen({ snapshot, suggestions, priming, contextLabel, apply, charterDrafting, charterDraftComplete } = {}) {
  const { setScreenOverride } = useRex();
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const snapshotKey = snapshot ? JSON.stringify(snapshot) : '';

  const suggestionsKey = Array.isArray(suggestions) ? suggestions.join('|') : '';
  const applyKey = apply?.enabled || apply?.label
    ? `${apply.enabled ? 'on' : 'off'}|${apply.label || 'apply'}`
    : '';
  const charterDraftKey = `${charterDrafting ? '1' : '0'}|${charterDraftComplete ? '1' : '0'}`;

  useEffect(() => {
    if (!snapshotKey && !suggestionsKey && !priming && !contextLabel && !applyKey && charterDraftKey === '0|0') {
      setScreenOverride(null);
      return undefined;
    }
    const currentApply = applyRef.current;
    setScreenOverride({
      snapshot: snapshot || null,
      suggestions: suggestions || null,
      priming: priming || null,
      contextLabel: contextLabel || null,
      apply: currentApply?.enabled ? currentApply : null,
      charterDrafting: Boolean(charterDrafting),
      charterDraftComplete: Boolean(charterDraftComplete),
    });
    return () => setScreenOverride(null);
    // snapshot/suggestions keyed via snapshotKey/suggestionsKey to avoid object identity churn
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotKey, suggestionsKey, priming, contextLabel, applyKey, charterDraftKey, setScreenOverride]);
}
